import { Navigate, Route, Routes } from 'react-router-dom'

import { AuthGuard } from '@/access/AuthGuard'
import { ProjectRedirect } from '@/components/px/ProjectRedirect'
import { RoleGuarded } from '@/access/RoleGuarded'
import { AppProviders } from '@/providers/AppProviders'
import App from '@/App'
import ArchitectureMapPage from '@/pages/ArchitectureMapPage'
import DesignsPage from '@/pages/DesignsPage'
import HomePage from '@/pages/HomePage'
import LoginPage from '@/pages/LoginPage'
import OAuthConsentPage from '@/features/oauth-consent/pages/OAuthConsentPage'
import OnboardingStep01Page from '@/pages/onboarding/OnboardingStep01Page'
import AcceptInvitePage from '@/pages/AcceptInvitePage'
import SsoCallbackPage from '@/pages/SsoCallbackPage'
import RoleLanding from '@/pages/RoleLanding'
import AuditLogPage from '@/pages/admin/AuditLogPage'
import OrgSettingsPage from '@/pages/admin/OrgSettingsPage'
import PeopleAdminPage from '@/pages/admin/PeopleAdminPage'
import ModulesPage from '@/pages/admin/ModulesPage'
import ConnectorsPage from '@/pages/admin/ConnectorsPage'
import OAuthCallbackPage from '@/pages/admin/OAuthCallbackPage'
import RuntimePage from '@/pages/admin/RuntimePage'
import ClientsRegisterPage from '@/pages/clients/ClientsRegisterPage'
import MessagesPage from '@/pages/MessagesPage'
import NotificationsPage from '@/pages/NotificationsPage'
import AdminProjectsPage from '@/pages/admin/AdminProjectsPage'
import SuperAdminClientsPage from '@/pages/admin/SuperAdminClientsPage'
import ClientPortfolioPage from '@/pages/my/ClientPortfolioPage'
import ManagerLandingPage from '@/pages/my/ManagerLandingPage'
import MyBoardPage from '@/pages/my/MyBoardPage'
import ClientPortalPage from '@/pages/portal/ClientPortalPage'
// PX-W6.4 (TASK-191D0FE9): the commercial spine's own-work surfaces.
import TimesheetPage from '@/pages/my/TimesheetPage'
import ApprovalsPage from '@/pages/my/ApprovalsPage'
import RateCardsPage from '@/pages/admin/RateCardsPage'
import AgentConfigPage from '@/pages/library/AgentConfigPage'
import AgentGuardrailsPage from '@/pages/library/AgentGuardrailsPage'
import AgentLibraryPage from '@/pages/library/AgentLibraryPage'
import BlueprintConfigPage from '@/pages/library/BlueprintConfigPage'
import PlaybookConfigPage from '@/pages/library/PlaybookConfigPage'
import RequirementsLibraryPage from '@/pages/library/RequirementsLibraryPage'
import TemplateDetailPage from '@/pages/library/TemplateDetailPage'
import TemplateLibraryPage from '@/pages/library/TemplateLibraryPage'
import BlueprintPage from '@/pages/project/BlueprintPage'
import BuildBoardPage from '@/pages/project/BuildBoardPage'
// PX-W6.4: the project's commercial surfaces.
import ProjectBudgetPage from '@/pages/project/ProjectBudgetPage'
import AgentSpendPage from '@/pages/project/AgentSpendPage'
import GoLivePage from '@/pages/project/GoLivePage'
import InterviewsPage from '@/pages/project/InterviewsPage'
import MaintainPage from '@/pages/project/MaintainPage'
import MigrationPage from '@/pages/project/MigrationPage'
import ProjectOverviewPage from '@/pages/project/ProjectOverviewPage'
import RequirementsRegisterPage from '@/pages/project/RequirementsRegisterPage'
import TestUatPage from '@/pages/project/TestUatPage'
import ClientsAmsPage from '@/pages/projects/ClientsAmsPage'
import PortfolioBoardPage from '@/pages/projects/PortfolioBoardPage'
import PortfolioHealthPage from '@/pages/projects/PortfolioHealthPage'
import AllocationPage from '@/pages/resources/AllocationPage'
import BadgesPage from '@/pages/resources/BadgesPage'
import CapacityForecastPage from '@/pages/resources/CapacityForecastPage'
import PersonProfilePage from '@/pages/resources/PersonProfilePage'
import ResourcePoolPage from '@/pages/resources/ResourcePoolPage'
import SkillsMatrixPage from '@/pages/resources/SkillsMatrixPage'
import TalentPipelinePage from '@/pages/resources/TalentPipelinePage'
import TrainingCenterPage from '@/pages/resources/TrainingCenterPage'
import UtilisationPage from '@/pages/resources/UtilisationPage'

/**
 * The route manifest and the route tree, in ONE file, because they are one
 * contract: `routes.test.tsx` asserts that every rail item anywhere resolves
 * to exactly one `ROUTE_PATHS` entry, so the list below cannot silently drift
 * from the JSX beneath it (I-2 / AC-1.6).
 *
 * ⚠ Guard order is fixed and is an invariant (I-5): `AuthGuard` FIRST (the
 * session gate — redirect semantics; it also redirects an authenticated
 * zero-org registrant to `/onboarding/01` before role gating), then
 * `RoleGuarded` (the role gate — in-place refusal). `/login`,
 * `/oauth/consent`, `/onboarding/01`, `/accept-invite` and `/auth/callback`
 * are the only routes wrapped by neither, and the only ones rendered outside
 * `AppShell`.
 *
 * W6.1: `AppProviders` wraps the tree (i18n boot + the org WebSocket), so
 * `main.tsx` stays untouched.
 */

/**
 * Every declared absolute path, params as `:projectId` / `:personId` /
 * `:templateId` / `:agentId`. The redirect-only paths are INCLUDED — existing
 * rail items already link to them, so leaving them out would fail I-2 on
 * correct code.
 */
export const ROUTE_PATHS: readonly string[] = [
  '/',
  '/home',
  '/login',
  '/oauth/consent',
  '/onboarding/01',
  '/accept-invite',
  '/auth/callback',
  '/portal',
  '/designs',
  '/architecture',

  '/my/board',
  '/my/project',
  '/my/team',
  '/my/client',

  // PX-W6.4 surfaces (TASK-191D0FE9) — the commercial spine.
  '/my/timesheet',
  '/my/approvals',
  '/admin/rates',
  '/project/:projectId/budget',
  '/project/:projectId/agent-spend',

  '/admin/projects',
  '/admin/clients',

  // PX-W6.2 surfaces (TASK-A7B7080E).
  '/admin/people',
  '/admin/settings',
  '/admin/audit',
  '/clients/register',
  '/notifications',
  '/messages',

  // PX-W6.5 surfaces (TASK-AAB07DC3) — the configuration surfaces: module
  // catalogue, connectors (+ the OAuth SPA callback child), runtime.
  '/admin/modules',
  '/admin/connectors',
  '/admin/connectors/callback',
  '/admin/runtime',

  '/resources',
  '/resources/pool',
  '/resources/people',
  '/resources/people/:personId',
  '/resources/capacity',
  '/resources/allocations',
  '/resources/skills',
  '/resources/utilisation',
  '/resources/pipeline',
  '/resources/training',
  '/resources/badges',

  '/projects',
  '/projects/health',
  '/clients',

  '/library',
  '/library/templates',
  '/library/templates/:templateId',
  '/library/requirements',
  '/library/agents',
  '/library/agents/:agentId',
  '/library/agents/:agentId/guardrails',
  '/library/blueprints',
  '/library/playbooks',

  '/project',
  '/project/:projectId',
  '/project/:projectId/interviews',
  '/project/:projectId/requirements',
  '/project/:projectId/blueprint',
  '/project/:projectId/build',
  '/project/:projectId/migration',
  '/project/:projectId/test',
  '/project/:projectId/go-live',
  '/project/:projectId/ams',
] as const

/** Session gate, then role gate — in that order, for every route but the
 * unguarded ones (`/login`, `/oauth/consent`, `/onboarding/01`,
 * `/accept-invite`, `/auth/callback`). */
const guarded = (element: React.ReactNode) => (
  <AuthGuard>
    <RoleGuarded>{element}</RoleGuarded>
  </AuthGuard>
)

export function AppRoutes() {
  return (
    <AppProviders>
      <Routes>
        {/* The three surfaces outside the shell, and the only routes without guards. */}
        <Route path="login" element={<LoginPage />} />
        {/*
         * The OAuth 2.1 consent screen (PX-W4.8). It must sit OUTSIDE the
         * prototype shell: it carries its own credential step against the real
         * backend, and the session the guards read means nothing to the
         * authorization server.
         */}
        <Route path="oauth/consent" element={<OAuthConsentPage />} />
        {/*
         * Onboarding step 01 (PX-W6.1): register + create the first
         * organisation. Unguarded by design — a registrant has no session
         * yet, and the backend's first-org bootstrap arm is the gate that
         * matters here.
         */}
        <Route path="onboarding/01" element={<OnboardingStep01Page />} />
        {/*
         * The invitation email's landing page (PX-W6.2 manual-pass gap):
         * unguarded by design — the token in the query IS the credential, an
         * invitee has no session yet.
         */}
        <Route path="accept-invite" element={<AcceptInvitePage />} />
        {/*
         * The SSO callback landing (CR-SSO-INVITE-PX-2026-09-26): the server's
         * OAuth callback returns the browser here with a one-time code or an
         * error key. Unguarded by design — the session does not exist yet; this
         * page is what establishes it.
         */}
        <Route path="auth/callback" element={<SsoCallbackPage />} />

        <Route element={<App />}>
        <Route index element={guarded(<RoleLanding />)} />
        <Route path="home" element={guarded(<HomePage />)} />
        <Route path="portal" element={guarded(<ClientPortalPage />)} />
        <Route path="designs" element={guarded(<DesignsPage />)} />
        <Route path="architecture" element={guarded(<ArchitectureMapPage />)} />

        <Route path="my">
          <Route path="board" element={guarded(<MyBoardPage scope="multi" />)} />
          <Route path="project" element={guarded(<MyBoardPage scope="single" />)} />
          <Route path="team" element={guarded(<ManagerLandingPage />)} />
          <Route path="client" element={guarded(<ClientPortfolioPage />)} />
          {/* PX-W6.4: the commercial spine's own-work surfaces. */}
          <Route path="timesheet" element={guarded(<TimesheetPage />)} />
          <Route path="approvals" element={guarded(<ApprovalsPage />)} />
        </Route>

        <Route path="admin">
          <Route path="projects" element={guarded(<AdminProjectsPage />)} />
          <Route path="clients" element={guarded(<SuperAdminClientsPage />)} />
          {/* PX-W6.2: people administration, settings, audit. */}
          <Route path="people" element={guarded(<PeopleAdminPage />)} />
          <Route path="settings" element={guarded(<OrgSettingsPage />)} />
          <Route path="audit" element={guarded(<AuditLogPage />)} />
          {/* PX-W6.4: the org's rate cards (admin-only destination). */}
          <Route path="rates" element={guarded(<RateCardsPage />)} />
          {/* PX-W6.5: the configuration surfaces — module catalogue, outbound
              credentials (+ the OAuth SPA callback child route), runtime
              (LLM config + scheduler + context index). */}
          <Route path="modules" element={guarded(<ModulesPage />)} />
          <Route path="connectors" element={guarded(<ConnectorsPage />)} />
          <Route path="connectors/callback" element={guarded(<OAuthCallbackPage />)} />
          <Route path="runtime" element={guarded(<RuntimePage />)} />
        </Route>

        <Route path="resources">
          <Route index element={<Navigate to="/resources/pool" replace />} />
          <Route path="pool" element={guarded(<ResourcePoolPage />)} />
          {/* PX-W6.2: no static demo slug in the route table — the exact path
              lands on the real directory. */}
          <Route path="people" element={<Navigate to="/resources/pool" replace />} />
          <Route path="people/:personId" element={guarded(<PersonProfilePage />)} />
          <Route path="capacity" element={guarded(<CapacityForecastPage />)} />
          <Route path="allocations" element={guarded(<AllocationPage />)} />
          <Route path="skills" element={guarded(<SkillsMatrixPage />)} />
          <Route path="utilisation" element={guarded(<UtilisationPage />)} />
          <Route path="pipeline" element={guarded(<TalentPipelinePage />)} />
          <Route path="training" element={guarded(<TrainingCenterPage />)} />
          <Route path="badges" element={guarded(<BadgesPage />)} />
        </Route>

        <Route path="projects" element={guarded(<PortfolioBoardPage />)} />
        <Route path="projects/health" element={guarded(<PortfolioHealthPage />)} />
        <Route path="clients" element={guarded(<ClientsAmsPage />)} />
        {/* PX-W6.2: the REAL client register — the demo `/clients` (Clients &
            AMS, mockup 2.11) stays untouched for W6.4. */}
        <Route path="clients/register" element={guarded(<ClientsRegisterPage />)} />

        {/* PX-W6.2: the notification centre + the org thread. */}
        <Route path="notifications" element={guarded(<NotificationsPage />)} />
        <Route path="messages" element={guarded(<MessagesPage />)} />

        <Route path="library">
          <Route index element={<Navigate to="/library/templates" replace />} />
          <Route path="templates" element={guarded(<TemplateLibraryPage />)} />
          <Route path="templates/:templateId" element={guarded(<TemplateDetailPage />)} />
          <Route path="requirements" element={guarded(<RequirementsLibraryPage />)} />
          <Route path="agents" element={guarded(<AgentLibraryPage />)} />
          <Route path="agents/:agentId" element={guarded(<AgentConfigPage />)} />
          <Route path="agents/:agentId/guardrails" element={guarded(<AgentGuardrailsPage />)} />
          <Route path="blueprints" element={guarded(<BlueprintConfigPage />)} />
          <Route path="playbooks" element={guarded(<PlaybookConfigPage />)} />
        </Route>

        <Route path="project">
          {/* PX-W6.6: no demo slug in a product route — the index resolves the
              caller's first LIVE engagement, else the portfolio. */}
          <Route index element={guarded(<ProjectRedirect />)} />
          <Route path=":projectId" element={guarded(<ProjectOverviewPage />)} />
          <Route path=":projectId/interviews" element={guarded(<InterviewsPage />)} />
          <Route path=":projectId/requirements" element={guarded(<RequirementsRegisterPage />)} />
          <Route path=":projectId/blueprint" element={guarded(<BlueprintPage />)} />
          <Route path=":projectId/build" element={guarded(<BuildBoardPage />)} />
          <Route path=":projectId/migration" element={guarded(<MigrationPage />)} />
          <Route path=":projectId/test" element={guarded(<TestUatPage />)} />
          <Route path=":projectId/go-live" element={guarded(<GoLivePage />)} />
          <Route path=":projectId/ams" element={guarded(<MaintainPage />)} />
          {/* PX-W6.4: the project's commercial surfaces — the agreed budget
              with its roll-up, and the AI-run spend metering. */}
          <Route path=":projectId/budget" element={guarded(<ProjectBudgetPage />)} />
          <Route path=":projectId/agent-spend" element={guarded(<AgentSpendPage />)} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AppProviders>
  )
}
